<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <h4 class="text-capitalize fw-500 breadcrumb-title">
                            <router-link to="/support"><i class="fas fa-arrow-left"></i></router-link>
                            &nbsp; Detail Tiket
                        </h4>
                        <div v-if="ticket" class="action-btn">
                            <span class="status-badge mr-2" :class="'status-' + ticket.status">{{ statusLabel(ticket.status) }}</span>
                            <button v-if="ticket.status !== 'closed'" @click="closeTicket" class="btn btn-light btn-sm">
                                <i class="fas fa-check"></i> Tutup Tiket
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="loading" class="text-center p-5"><div class="spinner-border"></div></div>
            <div v-else-if="!ticket" class="alert alert-danger">Tiket tidak ditemukan</div>
            <div v-else class="row">
                <div class="col-lg-8">
                    <!-- Ticket info card -->
                    <div class="card p-3 mb-3">
                        <h5>{{ ticket.subject }}</h5>
                        <div class="text-muted small">
                            <span><i class="fas fa-hashtag"></i> {{ ticket.ticket_number }}</span>
                            <span class="ml-3"><i class="fas fa-folder"></i> {{ categoryLabel(ticket.category) }}</span>
                            <span class="ml-3"><i class="fas fa-flag"></i> {{ priorityLabel(ticket.priority) }}</span>
                            <span class="ml-3"><i class="fas fa-clock"></i> {{ formatDate(ticket.created_at) }}</span>
                        </div>
                    </div>

                    <!-- Messages thread -->
                    <div class="card p-3 mb-3 thread-card">
                        <div v-for="m in messages" :key="m.id" :class="['message-row', 'sender-' + m.sender_type]">
                            <div class="message-bubble" :class="'bubble-' + m.sender_type">
                                <div class="message-header">
                                    <strong>{{ senderLabel(m.sender_type) }}</strong>
                                    <small class="text-muted ml-2">{{ formatDate(m.created_at) }}</small>
                                </div>
                                <div class="message-body" v-html="formatMessage(m.message)"></div>
                                <div v-if="parseAttachments(m.attachments).length > 0" class="message-attachments">
                                    <div v-for="(a, i) in parseAttachments(m.attachments)" :key="i" class="attachment-item">
                                        <img v-if="isImage(a.mime)" :src="a.url" :alt="a.filename" class="attach-image" @click="openLightbox(a.url)">
                                        <video v-else-if="isVideo(a.mime)" controls :src="a.url" class="attach-video"></video>
                                        <a v-else :href="a.url" target="_blank" class="attach-file">
                                            <i :class="fileIcon(a.mime)"></i>
                                            {{ a.filename }}
                                            <small class="text-muted">({{ formatSize(a.size) }})</small>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Reply form -->
                    <div v-if="ticket.status !== 'closed'" class="card p-3">
                        <h6><i class="fas fa-reply"></i> Balas</h6>
                        <textarea v-model="replyText" class="form-control" rows="4" placeholder="Tulis balasan Anda..."></textarea>
                        <div class="mt-2">
                            <input ref="replyFiles" type="file" multiple @change="onFileSelect" class="form-control-file" accept="*/*">
                            <div v-if="replyFiles.length > 0" class="mt-2">
                                <div v-for="(f, i) in replyFiles" :key="i" class="d-flex justify-content-between align-items-center small p-1" style="background:#f9fafb; border-radius:4px; margin-bottom:4px;">
                                    <span><i class="fas fa-paperclip"></i> {{ f.name }} <span class="text-muted">({{ formatSize(f.size) }})</span></span>
                                    <button @click="removeFile(i)" class="btn btn-sm btn-link text-danger p-0"><i class="fas fa-times"></i></button>
                                </div>
                            </div>
                        </div>
                        <div class="text-right mt-3">
                            <button @click="sendReply" :disabled="sending || !replyText.trim()" class="btn btn-primary">
                                <span v-if="sending"><i class="fas fa-spinner fa-spin"></i> Mengirim...</span>
                                <span v-else><i class="fas fa-paper-plane"></i> Kirim Balasan</span>
                            </button>
                        </div>
                    </div>
                    <div v-else class="alert alert-secondary text-center">
                        <i class="fas fa-lock"></i> Tiket sudah ditutup. Buat tiket baru kalau ada masalah lain.
                    </div>
                </div>

                <div class="col-lg-4">
                    <div class="card p-3">
                        <h6>Info Tiket</h6>
                        <table class="table table-sm small mb-0">
                            <tbody>
                                <tr><td class="text-muted">Status</td><td><span class="status-badge" :class="'status-' + ticket.status">{{ statusLabel(ticket.status) }}</span></td></tr>
                                <tr><td class="text-muted">Kategori</td><td>{{ categoryLabel(ticket.category) }}</td></tr>
                                <tr><td class="text-muted">Priority</td><td>{{ priorityLabel(ticket.priority) }}</td></tr>
                                <tr><td class="text-muted">Dibuat</td><td>{{ formatDate(ticket.created_at) }}</td></tr>
                                <tr v-if="ticket.last_admin_reply_at"><td class="text-muted">Reply admin</td><td>{{ formatRelative(ticket.last_admin_reply_at) }}</td></tr>
                                <tr v-if="ticket.closed_at"><td class="text-muted">Ditutup</td><td>{{ formatDate(ticket.closed_at) }}</td></tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
export default {
    data() {
        return {
            loading: false,
            sending: false,
            ticket: null,
            messages: [],
            replyText: '',
            replyFiles: [],
        };
    },
    mounted() { this.loadDetail(); },
    methods: {
        async loadDetail() {
            this.loading = true;
            try {
                const id = this.$route.params.id;
                const r = await this.$store.dispatch('support_ticket_detail', id);
                this.ticket = r.data.data.ticket;
                this.messages = r.data.data.messages || [];
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Gagal load tiket');
            }
            this.loading = false;
        },
        onFileSelect(e) {
            const files = Array.from(e.target.files || []);
            for (const f of files) {
                if (f.size > 25 * 1024 * 1024) {
                    this.$toast.error(`File "${f.name}" melebihi 25MB`);
                    continue;
                }
                if (this.replyFiles.length >= 5) {
                    this.$toast.error('Maksimal 5 file per pesan');
                    break;
                }
                this.replyFiles.push(f);
            }
            e.target.value = '';
        },
        removeFile(i) { this.replyFiles.splice(i, 1); },
        async sendReply() {
            if (!this.replyText.trim()) return;
            this.sending = true;
            try {
                const fd = new FormData();
                fd.append('message', this.replyText.trim());
                this.replyFiles.forEach(f => fd.append('files', f));
                const r = await this.$store.dispatch('support_ticket_reply', { id: this.ticket.id, formData: fd });
                this.messages.push(r.data.data.message);
                this.replyText = '';
                this.replyFiles = [];
                this.$toast.success('Balasan terkirim');
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Gagal kirim balasan');
            }
            this.sending = false;
        },
        async closeTicket() {
            if (!confirm('Tutup tiket ini? Anda tidak bisa balas lagi setelah ditutup.')) return;
            try {
                await this.$store.dispatch('support_ticket_close', this.ticket.id);
                this.$toast.success('Tiket ditutup');
                this.loadDetail();
            } catch (e) {
                this.$toast.error('Gagal tutup tiket');
            }
        },
        senderLabel(t) {
            return { user: 'Anda', admin: 'Tim Support StarSender', system: 'Sistem' }[t] || t;
        },
        statusLabel(s) {
            return { open: 'Open', in_progress: 'Diproses', waiting_user: 'Menunggu Anda', closed: 'Selesai' }[s] || s;
        },
        categoryLabel(c) {
            return { general: 'Umum', bug: 'Bug', billing: 'Pembayaran', feature: 'Saran Fitur', other: 'Lainnya' }[c] || c;
        },
        priorityLabel(p) {
            return { low: 'Rendah', normal: 'Normal', high: 'Tinggi', urgent: 'Urgent' }[p] || p;
        },
        formatDate(ts) { return ts ? moment(ts).format('DD MMM YYYY HH:mm') : '-'; },
        formatRelative(ts) { return ts ? moment(ts).fromNow() : ''; },
        formatMessage(m) {
            // Convert URLs ke link + preserve newline
            const escaped = (m || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const linked = escaped.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
            return linked.replace(/\n/g, '<br>');
        },
        parseAttachments(raw) {
            if (!raw) return [];
            try {
                if (typeof raw === 'string') return JSON.parse(raw);
                return raw;
            } catch { return []; }
        },
        isImage(mime) { return (mime || '').startsWith('image/'); },
        isVideo(mime) { return (mime || '').startsWith('video/'); },
        fileIcon(mime) {
            mime = mime || '';
            if (mime.includes('pdf')) return 'far fa-file-pdf';
            if (mime.includes('word') || mime.includes('document')) return 'far fa-file-word';
            if (mime.includes('sheet') || mime.includes('excel')) return 'far fa-file-excel';
            if (mime.includes('zip') || mime.includes('rar')) return 'far fa-file-archive';
            if (mime.startsWith('audio/')) return 'far fa-file-audio';
            return 'far fa-file';
        },
        formatSize(n) {
            if (!n) return '';
            if (n < 1024) return n + ' B';
            if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
            return (n / (1024 * 1024)).toFixed(1) + ' MB';
        },
        openLightbox(url) {
            window.open(url, '_blank');
        },
    },
};
</script>

<style scoped>
.thread-card { max-height: 600px; overflow-y: auto; }
.message-row {
    display: flex;
    margin-bottom: 14px;
}
.sender-user { justify-content: flex-end; }
.sender-admin { justify-content: flex-start; }
.sender-system { justify-content: center; }
.message-bubble {
    max-width: 75%;
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 14px;
    line-height: 1.5;
}
.bubble-user { background: #1976d2; color: #fff; }
.bubble-admin { background: #f3f4f6; color: #1f2937; }
.bubble-system {
    background: #fef3c7;
    color: #78350f;
    font-style: italic;
    font-size: 12px;
    text-align: center;
}
.bubble-user .text-muted { color: rgba(255,255,255,0.8) !important; }
.bubble-user a { color: #fff; text-decoration: underline; }
.message-header { font-size: 12px; margin-bottom: 4px; }
.bubble-user .message-header strong { color: rgba(255,255,255,0.95); }
.message-attachments { margin-top: 8px; }
.attachment-item { margin-bottom: 8px; }
.attach-image {
    max-width: 240px;
    max-height: 180px;
    border-radius: 8px;
    cursor: pointer;
    display: block;
}
.attach-video {
    max-width: 320px;
    max-height: 240px;
    border-radius: 8px;
    display: block;
}
.attach-file {
    display: inline-block;
    background: rgba(0, 0, 0, 0.05);
    padding: 6px 10px;
    border-radius: 6px;
    color: inherit;
    text-decoration: none;
    font-size: 13px;
}
.bubble-user .attach-file { background: rgba(255, 255, 255, 0.15); color: #fff; }
.attach-file:hover { background: rgba(0, 0, 0, 0.1); text-decoration: none; }
.status-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
}
.status-open { background: #fef3c7; color: #92400e; }
.status-in_progress { background: #dbeafe; color: #1e40af; }
.status-waiting_user { background: #fce7f3; color: #9d174d; }
.status-closed { background: #d1fae5; color: #065f46; }
</style>
